import React, {
	useEffect, useMemo, useRef, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import Icon, { COLORS, ICONS } from '../../general-components/icon';
import { showAlert } from '../../general-components/alert-service';
import { SelectGroupOption } from '../../general-components/select-group';
import { GetUser } from '../../../connection/greenroom';
import { GreenroomState } from '../../../stores/types/types';
import type { Invitation } from '../../../stores/types/invitations';
import type { ShowSession } from '../../../stores/types/session';
import { isValidEmail } from '../../../utils/utils';
import SelectSmall from '../../general-components/select-small';
import SelectUsers from '../../general-components/select-users';
import Avatar from '../../general-components/avatar';
import type { Channel } from '../../../stores/types/home';
import { HOME_ROUTES } from '../../../stores/reducers/home';
import AvatarBubbleRow from '../../general-components/avatar-bubble-row';
import StartEndDatePicker, { START_END_DATE_PICKER_SIZE } from '../../general-components/start-end-date-picker';
import { CreateOrUpdateMeeting } from '../../../connection/meeting';
import { APP } from '../../../stores/session-status';
import * as SessionActions from '../../../stores/actions/session';
import { loadMeetingsStandaloneGreenroomProducer } from '../../../stores/actions/content';
import WaitingIndicator from '../../general-components/waiting-indicator';

export const PRESENTER_ROLE = {
	PRESENTER: 'presenter',
	VIEWER: 'viewer',
};

const inviteOptions: SelectGroupOption[] = [
	{ label: 'Presenter', value: false, description: 'can participate in streaming' },
	{ label: 'Viewer', value: true, description: 'can view and comment' },
];

const sessionOptions: SelectGroupOption[] = [
	{ label: 'Rehearsal', value: 1 },
	{ label: 'Tech-check', value: 2 },
	{ label: 'Recording', value: 3 },
];
export interface InvitedPresenter {
	email: string;
	first_name: string;
	last_name: string;
	avatar: string;
	role: string;
	gr_user?: number;
}

// NOTE: sessions === rehearsals and tech checks (when you see session terminology)
export interface Session {
	id?: number;
	session_type_id: number;
	start_time: Date;
	end_time: Date;
	title: string;
	channel_name: string;
	invites: InvitedPresenter[];
}

export interface Props {
	editMeeting: any;
	reloadMeetings(isNewMeeting: boolean, meetingChannel: number): void;
}

export default function MeetingModal(props: Props) {
	const token = useSelector((state: GreenroomState) => state.SessionReducer.token);
	const user = useSelector((state: GreenroomState) => state.SessionReducer.user);
	const grChannels = useSelector((state: GreenroomState) => state.SessionReducer.grChannels);
	const showSessionTypes = useSelector(
		(state: GreenroomState) => state.SessionReducer.showSessionTypes,
	);
	const invitations = useSelector((state: GreenroomState) => state.InvitationsReducer.invitations);
	const selectedChannel = useSelector((state: GreenroomState) => state.HomeReducer.selectedChannel);
	const currentRoute = useSelector((state: GreenroomState) => state.HomeReducer.currentRoute);

	const {
		editMeeting,
		reloadMeetings,
	} = props;

	const dispatch = useDispatch();

	const [meetingTitle, setMeetingTitle] = useState<string>('');
	const [meetingStartDate, setMeetingStartDate] = useState<Date | null>(null);
	const [meetingEndDate, setMeetingEndDate] = useState<Date | null>(null);
	const [sessions, setSessions] = useState<Session[]>([]);
	const [removedSessions, setRemovedSessions] = useState<Session[]>([]);
	const [channel, setChannel] = useState<Channel | undefined>();

	const [isSavingMeeting, setIsSavingMeeting] = useState<boolean>(false);
	const [addingNewSession, setAddingNewSession] = useState<boolean>(false);
	const [sessionEditing, setSessionEditing] = useState<Session | undefined>();
	const sessionEditingIndex = useRef<number | undefined>();
	const channelOptions = useRef<SelectGroupOption[] | undefined>();

	const [emailValid, setEmailValid] = useState(false);
	const emailField = useRef<HTMLInputElement | null>(null);
	const [meetingInvites, setMeetingInvites] = useState<InvitedPresenter[]>([]);
	const [newPresenterIsWatcher, setNewPresenterIsWatcher] = useState(false);

	const tempProfileInvites: InvitedPresenter[] = [];
	let dirtyInputEmailExists = false;

	function assignLocalMeetingState(producerFirstInList: InvitedPresenter) {
		// If the edit button is clicked on a meeting,
		// we need to set up all the local state variables to work
		// nicely with the UI and saving the meeting.

		setMeetingTitle(editMeeting.title);
		setMeetingStartDate(editMeeting.start_time);
		setMeetingEndDate(editMeeting.end_time);
		if (editMeeting.invites) {
			editMeeting.invites.forEach((invite: { watcher_only: any; role: string; }) => {
				if (invite.watcher_only) {
					invite.role = PRESENTER_ROLE.VIEWER;
				} else {
					invite.role = PRESENTER_ROLE.PRESENTER;
				}
			});
		}
		setMeetingInvites([producerFirstInList, ...editMeeting.invites as InvitedPresenter[]]);

		let nSessions = [];
		if (editMeeting.show_sessions) {
			nSessions = editMeeting.show_sessions.map((session: ShowSession) => {
				const meetingInvitations = currentRoute === HOME_ROUTES.DETAILS
					? invitations.map((invitation: Invitation) => invitation.profile)
					: [...editMeeting.invites];

				const filteredSessionInvites = meetingInvitations.filter(
					(invite: any) => session.invitations.some((invitation: any) => {
						if (typeof invitation === 'string') {
							return invite.email === invitation;
						}
						return invite.email === invitation.email;
					}),
				);

				return {
					...session,
					start_time: new Date(session.start_time),
					end_time: new Date(session.end_time),
					invites: filteredSessionInvites,
				} as ShowSession;
			}).filter((session: ShowSession) => session.session_type_id);
		}

		setSessions(nSessions);
	}

	useEffect(() => {
		// The producer sees themself first in the team invites list as "producer"
		const producerFirstInList = { ...user } as InvitedPresenter;

		// Used for dropdown on top right and only visible
		// if producer is a member of 2+ standalone GR channels
		// grChannels could = []
		if (grChannels.length) {
			channelOptions.current = grChannels.map((channel) => ({
				label: channel.name,
				value: channel.channel,
			} as SelectGroupOption));
		}

		if (editMeeting) {
			// Editing existing meeting, assign local state from meeting
			// and assign channel selected in the dropdown
			assignLocalMeetingState(producerFirstInList);

			const newChannel = grChannels.find((c) => c.channel === editMeeting.channel);

			if (newChannel) {
				setChannel(newChannel);
			}
		} else {
			// New meeting, producer only one in list
			setMeetingInvites([producerFirstInList]);

			// Attempt to set channel dropdown to selected channel,
			// otherwise set it to the first channel in producer's list
			const newChannel = grChannels.find((c) => c.channel === selectedChannel?.channel);
			if (newChannel) {
				setChannel(newChannel);
			} else if (grChannels[0]) {
				setChannel(grChannels[0]);
			}
		}
	}, [editMeeting]);

	async function addPresenter(e: React.FormEvent | null) {
		if (e) e.preventDefault();

		if (!emailValid) {
			return showAlert({
				message: 'Please enter a valid email address.',
				duration: 2000,
				type: 'warning',
			});
		}

		if (emailField.current) {
			const email = emailField.current?.value.trim();
			const exists = meetingInvites.some((invite: InvitedPresenter) => invite.email === email);
			if (exists) {
				return showAlert({
					message: 'User already invited.',
					duration: 2000,
					type: 'warning',
				});
			}
			// Check if invited presenter has logged into GR before so we can use their name + avatar
			let userProfile = await GetUser(token!, email);
			if (userProfile && userProfile.profile) {
				userProfile = userProfile.profile;
				userProfile.role = newPresenterIsWatcher ? PRESENTER_ROLE.VIEWER : PRESENTER_ROLE.PRESENTER;
				setMeetingInvites([...meetingInvites, userProfile]);
			} else {
				const newUser = {} as InvitedPresenter;
				newUser.email = email;
				newUser.role = newPresenterIsWatcher ? PRESENTER_ROLE.VIEWER : PRESENTER_ROLE.PRESENTER;
				setMeetingInvites([...meetingInvites, newUser]);
			}

			emailField.current.value = '';
			checkEmail(null);
		}
	}

	function checkEmail(e: React.ChangeEvent<HTMLInputElement> | null) {
		if (emailField.current) setEmailValid(isValidEmail(emailField.current!.value.trim()));
	}

	function removeInvitedPresenter(email: string) {
		setMeetingInvites(meetingInvites.filter((invite) => invite.email !== email));

		// Also remove presenter from sessions (rehearsals and tech checks)
		sessions.forEach((session) => {
			if (session.invites) {
				session.invites = session.invites.filter((invite) => invite.email !== email);
			}
		});
	}

	function changeExistingPresenterRole(index: number, isViewer: boolean) {
		meetingInvites[index].role = isViewer ? PRESENTER_ROLE.VIEWER : PRESENTER_ROLE.PRESENTER;
		setMeetingInvites([...meetingInvites]);
	}

	const invitedPresenterList = useMemo(() => meetingInvites.map(
		(p: InvitedPresenter, index: number) => (
			<div className="invite-presenter-row" key={p.email}>
				<Avatar
					noBorder={false}
					user={p}
					size={'thirtytwo'}
				/>
				<div className="presenter-name">
					{(p.first_name || p.last_name)
						&& <label>{p.first_name} {p.last_name}{index === 0 && <span>, you</span>}</label>
					}
				</div>
				<div className="presenter-email">
					<span>{p.email}</span>
				</div>
				<div className="presenter-controls">
					{index === 0
						? <span>Producer</span>
						:						<>
							<SelectSmall
								options={inviteOptions}
								onChange={(isViewer) => changeExistingPresenterRole(index, isViewer)}
								label={p.role === PRESENTER_ROLE.VIEWER ? 'Viewer' : 'Presenter'}
								selected={inviteOptions.find(
									(option) => option.value === (p.role === PRESENTER_ROLE.VIEWER),
								) || inviteOptions[0]}
								dropdownPosition={'drop-down-left'}
								dropdown={true}
								optionHeight={60}
								openAtSelected={false}
							/>
							<button onClick={() => removeInvitedPresenter(p.email)}>
								<Icon name={ICONS.CLOSE} size={12} color={COLORS.DARK_GRAY} />
							</button>
						</>
					}
				</div>
			</div>
		),
	), [meetingInvites]);

	function changeNewPresenterRole(isWatcherOnly: boolean) {
		if (isWatcherOnly === newPresenterIsWatcher) { return; }
		setNewPresenterIsWatcher(isWatcherOnly);
	}

	function saveSession() {
		if (addingNewSession) {
			setSessions([...sessions, sessionEditing!]);
			setAddingNewSession(false);
		} else {
			sessions[sessionEditingIndex.current!] = sessionEditing!;
			sessionEditingIndex.current = undefined;
			setSessions(sessions);
		}
		setSessionEditing(undefined);
	}

	function editSession(session: Session, index: number) {
		sessionEditingIndex.current = index;
		setSessionEditing({ ...session });
	}

	function removeSession(index: number) {
		const updatedSessions = [...sessions];
		const removed = updatedSessions.splice(index, 1);
		setRemovedSessions([...removedSessions, ...removed]);
		setSessions(updatedSessions);
	}

	function cancelAddEditSession() {
		if (addingNewSession) {
			setAddingNewSession(false);
		} else {
			sessionEditingIndex.current = undefined;
		}
		setSessionEditing(undefined);
	}

	function openAddSession() {
		const roundedStart = new Date(new Date().getTime() + 30 * 60000);
		roundedStart.setMilliseconds(Math.round(roundedStart.getMilliseconds() / 1000) * 1000);
		roundedStart.setSeconds(Math.round(roundedStart.getSeconds() / 60) * 60);
		roundedStart.setMinutes(Math.round(roundedStart.getMinutes() / 5) * 5);

		const session = {
			session_type_id: 1,
			title: '',
			start_time: roundedStart,
			end_time: new Date(roundedStart.getTime() + 30 * 60000),
			invites: [],
			channel_name: '',
		} as Session;
		setSessionEditing(session);
		setAddingNewSession(true);
	}

	function meetingRequiredFieldsFilledOut() {
		if (meetingTitle && meetingStartDate && meetingEndDate) {
			return true;
		}
		return false;
	}

	function sessionRequiredFieldsFilledOut() {
		if (sessionEditing?.title) {
			return true;
		}
		return false;
	}

	async function handleDirtyEmailInput() {
		if (!emailValid) {
			return showAlert({
				message: 'Please enter a valid email address.',
				duration: 2000,
				type: 'warning',
			});
		}

		if (emailField.current) {
			tempProfileInvites.push(...meetingInvites);
			const email = emailField.current?.value.trim();
			const exists = meetingInvites.some((invite: InvitedPresenter) => invite.email === email);
			if (exists) {
				dirtyInputEmailExists = true;
				return showAlert({
					message: 'User already invited.',
					duration: 2000,
					type: 'warning',
				});
			}
			// Check if invited presenter has logged into GR before so we can use their name + avatar
			let userProfile = await GetUser(token!, email);
			if (userProfile && userProfile.profile) {
				userProfile = userProfile.profile;
				userProfile.role = newPresenterIsWatcher ? PRESENTER_ROLE.VIEWER : PRESENTER_ROLE.PRESENTER;
				tempProfileInvites.push(userProfile);
			} else {
				const newUser = {} as InvitedPresenter;
				newUser.email = email;
				newUser.role = newPresenterIsWatcher ? PRESENTER_ROLE.VIEWER : PRESENTER_ROLE.PRESENTER;
				tempProfileInvites.push(newUser);
			}
			emailField.current.value = '';
		}
	}

	async function saveMeeting() {
		if (!token || !channel || !meetingStartDate || !meetingEndDate) return;
		setIsSavingMeeting(true);

		if (emailField.current?.value !== '') {
			await handleDirtyEmailInput();
			if (dirtyInputEmailExists) {
				dirtyInputEmailExists = false;
				// @ts-ignore
				emailField.current.value = '';
				return;
			}
		}

		// Remove producer from list of meeting invites. They don't invite themselves
		// and they don't receive an email. Just there for UI
		// to see that they are the producer for this new GR they are creating.
		const invitesToUse = tempProfileInvites.length > 0 ? tempProfileInvites : meetingInvites;
		invitesToUse.shift();

		const meeting = editMeeting ? editMeeting.meeting : undefined;
		const meetingID = editMeeting ? editMeeting.meetind_id : undefined;
		const meetingObj = {
			channel: channel.channel,
			start_time: meetingStartDate.getTime(),
			end_time: meetingEndDate.getTime(),
			language: editMeeting?.language ?? null,
		};
		const meetingResult = await CreateOrUpdateMeeting(
			token,
			meeting,
			meetingObj,
			APP.GREENROOM,
			meetingTitle,
			invitesToUse,
			undefined,
			undefined,
			meetingID,
		);

		sessions.forEach((session) => {
			const newShowSession: ShowSession = {
				session_type_id: session.session_type_id,
				meeting: meetingResult.meeting,
				start_time: session.start_time,
				end_time: session.end_time,
				id: session?.id ? session.id : 0,
				title: session.title,
				invitations: session.invites.map((invitation) => invitation.email),
			};

			if (newShowSession.id) {
				dispatch(SessionActions.editShowSession(token, newShowSession));
			} else {
				dispatch(SessionActions.addShowSession(token, newShowSession));
			}
		});

		removedSessions.forEach((session) => {
			const removedShowSession: ShowSession = {
				session_type_id: session.session_type_id,
				meeting: meetingResult.meeting,
				start_time: session.start_time,
				end_time: session.end_time,
				id: session?.id ? session.id : 0,
				title: session.title,
				invitations: session.invites.map((invitation) => invitation.email),
			};

			if (removedShowSession.id) {
				dispatch(SessionActions.deleteShowSession(token, removedShowSession));
			}
		});

		reloadMeetings(!editMeeting, channel.channel);
		dispatch(SessionActions.setMeetingModalOpen(false));
		dispatch(loadMeetingsStandaloneGreenroomProducer(token));

		if (isSavingMeeting) {
			setIsSavingMeeting(false);
		}
	}

	function cancelAddEditMeeting() {
		dispatch(SessionActions.setMeetingModalOpen(false));
	}

	function channelChanged(value: number) {
		const newChannel = grChannels.find((c) => c.channel === value);

		if (newChannel) {
			setChannel(newChannel);
		}
	}

	function sessionForm() {
		// Producer doesn't see themself in the session invites dropdown
		const invitesMinusProducer = meetingInvites.slice(1);
		const sessionOptionsBroadcast = sessionEditing?.session_type_id === 4 ? [
			...sessionOptions,
			{ label: 'Broadcast', value: 4 },
		] : sessionOptions;

		return (
			<div key={'key'} className={classNames('add-rehearsal-card', !addingNewSession && 'editing')}>
				<div className="header">
					<span className="title">{`${addingNewSession ? 'Add' : 'Edit'} Session`}</span>
					<div className="buttons">
						<button className="button cancel" onClick={cancelAddEditSession}><span>Cancel</span></button>
						<button className="button save" onClick={saveSession} disabled={!sessionRequiredFieldsFilledOut()}><span>Save</span></button>
					</div>
				</div>
				<div className="input-container type">
					<span className="label" data-end="*">Type</span>
					<SelectSmall
						options={sessionOptionsBroadcast}
						onChange={(option) => setSessionEditing(
							{ ...sessionEditing!, session_type_id: option },
						)}
						label={sessionEditing!.session_type_id === 1 ? 'Rehearsal' : 'Tech-check'}
						selected={sessionOptionsBroadcast.find(
							(option) => option.value === sessionEditing!.session_type_id,
						) || sessionOptionsBroadcast[0]}
						dropdown={true}
						optionHeight={48}
						openAtSelected={false}
						dropdownPosition={'drop-down-right'}
					/>
				</div>

				<div className="input-container name">
					<span className="label" data-end="*">Title</span>
					<input className="input" type="text" value={sessionEditing?.title} onChange={(e) => setSessionEditing({ ...sessionEditing!, title: e.target.value })} />
				</div>

				<StartEndDatePicker
					startDate={sessionEditing!.start_time}
					endDate={sessionEditing!.end_time}
					datesUpdated={(startDate, endDate) => {
						setSessionEditing({ ...sessionEditing!, start_time: startDate, end_time: endDate });
					}}
					size={START_END_DATE_PICKER_SIZE.SMALL}
					defaultDurationMins={30}
				/>

				<div className="input-container">
					<span className="label" data-end="*">Team</span>
					<SelectUsers
						users={invitesMinusProducer}
						onChange={(selectedInvites) => setSessionEditing(
							{ ...sessionEditing!, invites: selectedInvites },
						)}
						selected={sessionEditing!.invites || []}
					/>
				</div>
			</div>
		);
	}

	function sessionCard(session: Session, index: number) {
		return (
			<div key={`${index}${session.title}`} className="session">
				<div className="date-title-container">
					<span className="date">
						{session.start_time.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' })}
						<span className="dot">·</span>
						{session.start_time.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
						{' - '}
						{session.end_time.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
					</span>
					<div className="title-tag-container">
						<span>{session.title}</span>
						<div className={classNames('tag', showSessionTypes[session.session_type_id - 1].name)}>
							<span>{showSessionTypes[session.session_type_id - 1].name}</span>
						</div>
					</div>
				</div>
				<div className="divider" />
				<div className="right-side-container">
					<AvatarBubbleRow invites={session.invites} size="xsmall" alignEnd={true} expandable={true} />
					<div className="buttons">
						<button onClick={() => editSession(session, index)} disabled={addingNewSession}>
							<Icon name={ICONS.EDIT} color={COLORS.WHITE} size={16} />
						</button>
						<button onClick={() => removeSession(index)}>
							<Icon name={ICONS.CLOSE} color={COLORS.WHITE} size={16} />
						</button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="modal-background">
			<div className="modal-container">
				<div className="new-meeting-modal">
					<div className="header">
						<span className="title">Show details</span>

						{user && grChannels && grChannels.length > 1 && channelOptions.current && channel
							&& <div className="input-container type channel">
								<span className="label text-align-right" data-end="*">Channel</span>

								<SelectSmall
									options={channelOptions.current}
									onChange={(option) => channelChanged(option)}
									label={channel?.name}
									selected={channelOptions.current.find(
										(option) => option.value === channel.channel,
									)!}
									dropdown={true}
									openAtSelected={false}
									dropdownPosition={'drop-down-left'}
								/>
							</div>
						}
					</div>

					<div className="input-container name">
						<span className="label" data-end="*">Title</span>
						<input
							className="input"
							data-testid="meeting-modal-meeting-title"
							type="text"
							value={meetingTitle}
							onChange={(e) => setMeetingTitle(e.target.value)}
						/>
					</div>

					<StartEndDatePicker
						startDate={editMeeting && new Date(editMeeting.start_time)}
						endDate={editMeeting && new Date(editMeeting.end_time)}
						datesUpdated={(startDate, endDate) => {
							setMeetingStartDate(startDate);
							setMeetingEndDate(endDate);
						}}
						size={START_END_DATE_PICKER_SIZE.NORMAL}
						defaultDurationMins={60}
					/>

					<div className="input-container presenters">
						<span className="label" data-end="*">Team</span>
						<form className="input-form" onSubmit={addPresenter}>
							<input
								data-testid="meeting-modal-team"
								type="text"
								onChange={checkEmail}
								placeholder="Enter email to invite..."
								ref={emailField}
							/>
							<SelectSmall
								options={inviteOptions}
								onChange={changeNewPresenterRole}
								label={newPresenterIsWatcher ? 'Viewer' : 'Presenter'}
								selected={
									inviteOptions.find(
										(option) => option.value === (!!newPresenterIsWatcher),
									) || inviteOptions[0]
								}
								dropdownPosition={'drop-down-left'}
								dropdown={true}
								optionHeight={60}
								openAtSelected={false}
							/>
						</form>

						<div className="invite-presenters-list">
							{invitedPresenterList}
						</div>
					</div>

					{sessions.map((session, index) => (sessionEditingIndex.current! === index
						? sessionForm()
						: sessionCard(session, index)
					))}

					{addingNewSession
						&& sessionForm()
					}

					{!addingNewSession
						&& <div onClick={() => openAddSession()} className={classNames('add-rehearsal-button', sessionEditing && 'disabled')}>
							<div className="green-circle">
								<Icon name={ICONS.ADD} size={15} color={COLORS.BLACK} />
							</div>
							<div className="message-container">
								<span className="title">Add Session</span>
								<span className="message">Schedule additional times to meet</span>
							</div>
						</div>
					}

					<div className="bottom-buttons">
						<button
							className="gray"
							data-testid="meeting-modal-cancel-save-meeting"
							disabled={isSavingMeeting}
							onClick={cancelAddEditMeeting}
						>
							Cancel
						</button>
						<button
							className="green"
							data-testid="meeting-modal-save-meeting"
							disabled={!channel
								|| isSavingMeeting
								|| !meetingRequiredFieldsFilledOut()
								// Disable meeting modal save button when a session is being added/edited
								|| sessionEditingIndex.current !== undefined
								|| addingNewSession
							}
							onClick={saveMeeting}
						>
							{isSavingMeeting ? <WaitingIndicator /> : 'Save'}
						</button>
					</div>

				</div>
			</div>
		</div>
	);
}
